// Today / Weekdays / Weekend / Month (docs/SPEC.md item 3; nowish.json tabs and tabOrder): equal gaps between the
// words, the selected one bold, and one underline for the row, exactly as wide as the selected word, sliding to it
// when the selection changes (web/dom.js segUnderline). The first time, and when the row resizes, it goes straight there.
import { useLayoutEffect, useRef } from "react";
import { SHARED } from "../../shared/config";
import { observeResize } from "../../shared/resize";
import { RULES } from "../../shared/rules";
import { useNow } from "../../state/clock";
import { setTab, useView } from "../../state/view";
import styles from "./TimeTabs.module.css";

export function TimeTabs() {
  const now = useNow(), time = useView((s) => s.filter.time);
  const order = RULES.tabOrder(now);
  const tabs = order.map((id) => SHARED.tabs.find((t) => t.id === id)!);
  const row = useRef<HTMLDivElement>(null), line = useRef<HTMLSpanElement>(null), placed = useRef(false);

  // Places the line under the selected word; `slide` animates it there (the CSS transition), else it jumps.
  const place = (slide: boolean) => {
    const box = row.current, ul = line.current;
    const word = box?.querySelector<HTMLElement>('button[aria-selected="true"] [data-word]');
    if (!box || !ul || !word) return;
    const w = word.getBoundingClientRect(), r = box.getBoundingClientRect();
    if (!w.width) return; // not laid out yet (hidden): the ResizeObserver places it once it shows
    const jump = !slide || !placed.current;
    ul.style.transition = jump ? "none" : "";
    ul.style.width = `${w.width}px`;
    ul.style.transform = `translateX(${w.left - r.left}px)`;
    if (jump) { void ul.offsetWidth; ul.style.transition = ""; } // jumped: the next change slides again
    placed.current = true;
  };
  const placeRef = useRef(place);
  placeRef.current = place;
  // A new tab (or a new order): slide there.
  useLayoutEffect(() => { placeRef.current(true); }, [time, order.join()]);
  // The row resizing (the panel, the window, fonts arriving): straight there. One observer for the row's life; its
  // first report is the size it already had.
  useLayoutEffect(() => {
    const box = row.current;
    if (!box) return;
    let last = -1;
    return observeResize(box, () => {
      const w = box.getBoundingClientRect().width;
      if (w !== last) { if (last >= 0 || !placed.current) placeRef.current(false); last = w; }
    });
  }, []);

  return (
    <div ref={row} id="time-filter" className={styles.row} role="tablist" aria-label="When">
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={time === t.id} data-time={t.id} data-label={t.title}
          className={styles.tab} onClick={() => setTab(t.id)}>
          <span data-word="">{t.title}</span>
        </button>
      ))}
      <span ref={line} className={styles.underline} aria-hidden="true" />
    </div>
  );
}
